import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Appearance } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { getTheme } from '../common/brand/theme';
import { getUiTheme, normalizeThemeMode, THEME_MODE } from './design/tokens';

export const colors = {
	PRIMARY: [
		'#203d54',
		'#1a4971',
		'#2368a2',
		'#3183c8',
		'#63a2d8',
		'#aad4f5',
		'#cde6f9',
		'#eff8ff',
	],
	SECONDARY: [
		'#ff8400',
		'#fc8c14',
		'#ff9320',
		'#ffa13d',
		'#ffb05b',
		'#ffc07c',
		'#ffe4c6',
	],
	GREY: [
		'#212934',
		'#272e36',
		'#5f6b7a',
		'#8895a7',
		'#b8c4ce',
		'#cfd6de',
		'#e1e7ec',
		'#f8f9fa',
		'#777',
	],
	ACCENT: [
		'#124544',
		'#1b655e',
		'#2a9187',
		'#3caea3',
		'#6ed7d3',
		'#a8eeeb',
		'#e7fffe',
	],
	YELLOW: [
		'#5c4813',
		'#8c6d1f',
		'#caa53d',
		'#f4ca64',
		'#fae29f',
		'#fdf3d7',
		'#fffcf4',
	],
	GREEN: [
		'#155239',
		'#187741',
		'#249d57',
		'#38c172',
		'#74d99f',
		'#a8eec1',
		'#e3fcec',
	],
	RED: [
		'#611818',
		'#891b1b',
		'#b82020',
		'#dc3030',
		'#e46464',
		'#f5aaaa',
		'#fce8e8',
	],
};

export const brand = {
	// PRIMARY BRAND COLORS
	grey1: '#e8e8e8',
	grey2: '#c9cdcf',
	grey3: '#999999',
	grey4: '#727272',

	blue: '#3b60c4',
	orange: '#fc8c14',
	black: '#272f3b',
	white: '#FFF',

	orangeGradient: 'linear-gradient(to bottom,#fc8c14 0,#f77d0e 100%)',

	// Semantic UI colors, adjusted

	semantic: {
		simple: {
			red: '#db2828',
			orange: '#f2711c',
			yellow: '#f5b600',
			olive: '#b5cc18',
			green: '#21ba45',
			teal: '#00b5ad',
			blue: '#2185d0',
			violet: '#6435c9',
			purple: '#a333c8',
			pink: '#e03997',
			brown: '#a5673f',
			grey: '#bcbcbc',
			black: '#1b1c1d',
		},
		hover: {
			red: '#d01919',
			orange: '#f26202',
			yellow: '#eaae00',
			olive: '#a7bd0d',
			green: '#16ab39',
			teal: '#009c95',
			blue: '#1678c2',
			violet: '#5829bb',
			purple: '#9627ba',
			pink: '#e61a8d',
			brown: '#975b33',
			grey: '#838383',
			black: '#27292a',
		},
	},

	// Healthy choices guidelines

	healthComplianceGreen: {
		color: '#33e2bc50',
		border: '1px solid #51d6b9',
	},
	healthComplianceEveryday: {
		color: '#33e2bc50',
		border: '1px solid #51d6b9',
	},
	healthComplianceAmber: {
		border: '1px solid #ffdb65',
		color: '#ffe96080',
	},
	healthComplianceRed: {
		border: '1px solid #d2929d',
		color: '#ff8c9e7a',
	},
	healthComplianceOccasional: {
		border: '1px solid #ddd',
		color: '#dddddd70',
	},
	healthComplianceNA: {
		border: '1px solid #afb3ff',
		color: '#afb3ff',
	},
};

// Shared font size scale — prefer these over per-screen literals in new code.
export const fontSizes = {
	xs: 11,
	sm: 12,
	md: 13,
	base: 14,
	lg: 16,
	xl: 20,
};

// const osMode = Appearance.getColorScheme() || 'light';

// Screens not yet restyled read the shared (web) theme keys. Onyx (saved as 'midnightPurple')
// keeps the retired 'dark' keys so those screens still render.
const LEGACY_THEME_MODE = {
	[THEME_MODE.LIGHT]: 'light',
	[THEME_MODE.DARK_BLUE]: 'darkBlue',
	[THEME_MODE.MIDNIGHT_PURPLE]: 'dark',
};

// Safety net for screens nobody restyled: the legacy keys that paint screen
// backgrounds, cards, headers, borders and text follow the redesign palette,
// so every screen sits on the new canvas and card colours in all three themes.
// The shared web values in src/common/brand/theme.js stay unchanged.
const getLegacyPaletteKeys = colors => ({
	mainBg1: colors.canvas, // screen containers, stack card background
	mainBg2: colors.chip, // selected tab, table header
	mainBg3: colors.card, // list items, banners, chips
	mainBg4: colors.canvas, // main app background
	mainBg5: colors.disabledBg, // disabled input, hover
	mainBg8: colors.raised, // tooltip
	mainBg9: colors.surface, // date picker, map controls
	mainBg10: colors.card, // number and edit-in-place fields
	mainBg12: colors.card, // tracks, chips
	mainBg15: colors.card, // buttons, pickers, pills
	mainBg16: colors.raised, // pressed buttons
	mainBg17: colors.card, // list header
	mainBg18: colors.surface, // dropdowns, menus
	mainBg20: colors.chip, // selected tab
	mainBg21: colors.chip, // hovered tab
	mainBg22: colors.chip, // selected vertical tab
	mainBg23: colors.surface, // dialogs
	mainBg24: colors.surface, // dialog header
	mainBg28: colors.surface, // stack header, raised page
	fakeListHeaderColor: colors.card,
	inputDisabledBg: colors.disabledBg,
	border1: colors.border,
	border2: colors.borderStrong,
	border3: colors.border,
	border4: colors.borderStrong,
	border5: colors.border,
	border6: colors.borderStrong,
	text1: colors.text,
	text2: colors.textSecondary,
	text3: colors.textSecondary,
	text11: colors.accent,
	text12: colors.textFaint,
	linkColor1: colors.accent,
});

/**
 * Theme object served by useTheme(): the legacy flat keys (theme.text1,
 * theme.mainBg1 ...) plus the redesign tokens under theme.ui (see design/tokens.js).
 */
export const getAppTheme = (mode, { areBrandFontsLoaded = false } = {}) => {
	const ui = getUiTheme(mode, { areBrandFontsLoaded });

	return {
		...getTheme(LEGACY_THEME_MODE[mode] || 'light'),
		...getLegacyPaletteKeys(ui.colors),
		ui,
	};
};

// Last theme the provider served, for UI rendered outside the React tree (web alerts).
let currentAppTheme = getAppTheme(THEME_MODE.LIGHT);
export const getCurrentAppTheme = () => currentAppTheme;

export const ThemeManagerContext = React.createContext({
	mode: THEME_MODE.LIGHT,
	theme: getAppTheme(THEME_MODE.LIGHT),
	toggle: () => {},
});

export const useTheme = () => useContext(ThemeManagerContext);

const THEME_MODE_STORAGE_KEY = 'themeMode';

const ThemeManagerProvider = ({ children, areBrandFontsLoaded = false }) => {
	const [mode, setMode] = useState(THEME_MODE.LIGHT);
	const [isLoading, setIsLoading] = useState(true);

	// Load saved theme preference on mount
	useEffect(() => {
		const loadThemePreference = async () => {
			try {
				const savedTheme = await AsyncStorage.getItem(THEME_MODE_STORAGE_KEY);
				const resolvedMode = normalizeThemeMode(savedTheme, Appearance.getColorScheme());
				setMode(resolvedMode);
				// Rewrite retired values (e.g. 'dark') so the migration runs once.
				if (savedTheme && savedTheme !== resolvedMode) {
					await AsyncStorage.setItem(THEME_MODE_STORAGE_KEY, resolvedMode);
				}
			} catch (error) {
				console.error('Error loading theme preference:', error);
			} finally {
				setIsLoading(false);
			}
		};
		loadThemePreference();
	}, []);

	// Listen to OS appearance changes
	useEffect(() => {
		const subscription = Appearance.addChangeListener(async ({ colorScheme }) => {
			// Only update if user hasn't set a preference
			const savedTheme = await AsyncStorage.getItem(THEME_MODE_STORAGE_KEY);
			if (!savedTheme) {
				setMode(normalizeThemeMode(null, colorScheme));
			}
		});
		return () => subscription?.remove();
	}, []);

	// Save theme preference when it changes
	const handleSetMode = useCallback(async (newMode) => {
		const resolvedMode = normalizeThemeMode(newMode, Appearance.getColorScheme());
		setMode(resolvedMode);
		try {
			await AsyncStorage.setItem(THEME_MODE_STORAGE_KEY, resolvedMode);
		} catch (error) {
			console.error('Error saving theme preference:', error);
		}
	}, []);

	const contextValue = useMemo(() => ({
		mode,
		theme: getAppTheme(mode, { areBrandFontsLoaded }),
		setMode: handleSetMode,
	}), [mode, areBrandFontsLoaded, handleSetMode]);

	useEffect(() => {
		currentAppTheme = contextValue.theme;
	}, [contextValue.theme]);

	if (isLoading) {
		return null; // Or a loading spinner
	}

	return (
		<ThemeManagerContext.Provider value={contextValue}>
			{children}
		</ThemeManagerContext.Provider>
	);
};

export default ThemeManagerProvider;
